import React, { useState, useEffect } from "react";
import { Typography, Input, Select, Progress, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { fetchPortfolioHealth } from "../DealerInsights/services/api";
import styles from "./InventoryCommandCenter.module.css";

const { Title } = Typography;
const { Option } = Select;

// Diamond Icon Component for Elite
const DiamondIcon = () => (
  <div className={styles.diamondIcon}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2L12 6L8 14L4 6L8 2Z" fill="currentColor" />
    </svg>
  </div>
);

// Metric Card Component
const MetricCard = ({ variant, title, units, capital, subtitle, badgeCount, hint, icon: Icon }) => {
  return (
    <div className={`${styles.metricCard} ${styles[`metricCard-${variant}`]}`}>
      <div className={styles.metricCardHeader}>
        <div className={styles.metricCardTitleWrap}>
          {variant === "elite" ? (
            <DiamondIcon />
          ) : (
            <Icon className={styles.metricCardIcon} />
          )}
          <span className={styles.metricCardTitle}>{title}</span>
        </div>
        {badgeCount && (
          <span className={styles.metricCardBadge}>{badgeCount}</span>
        )}
      </div>
      <div className={styles.metricCardMetrics}>
        <div className={styles.metricCardUnits}>
          {units}
          {variant === "strong" && <span className={styles.arrowUp}>↑</span>}
          {(variant === "watch" || variant === "risk") && <span className={styles.arrowDown}>↓</span>}
        </div>
        <div className={styles.metricCardCapital}>{capital}</div>
      </div>
      <div className={`${styles.metricCardSubtitle} ${styles[`subtitle-${variant}`]}`}>
        {subtitle}
      </div>
      <div className={styles.metricCardHint}>
        • {hint}
      </div>
    </div>
  );
};

// Inventory Row Component
const InventoryRow = ({ tier, vehicle, pricing, stats, adjust }) => {
  const tierConfig = {
    elite: { color: "#8b5cf6", label: "ELITE" },
    strong: { color: "#22c55e", label: "STRONG" },
    watch: { color: "#eab308", label: "WATCH" },
    risk: { color: "#ef4444", label: "RISK" },
  };

  const config = tierConfig[tier] || tierConfig.elite;

  return (
    <div className={`${styles.inventoryRow} ${styles[`row-${tier}`]}`}>
      <div className={styles.rowAccentBar} style={{ backgroundColor: config.color }} />
      <div className={styles.rowContent}>
        <div className={styles.rowVehicle}>
          <div className={styles.rowThumbnail}>
            <div className={styles.thumbnailPlaceholder} />
          </div>
          <div className={styles.rowVehicleInfo}>
            <div className={styles.rowVehicleTitle}>{vehicle.title}</div>
            <div className={styles.rowVehicleMeta}>
              {vehicle.miles} miles
            </div>
            <div className={styles.rowVehicleVin}>{vehicle.vin}</div>
            <Tag className={`${styles.tierBadge} ${styles[`badge-${tier}`]}`}>
              {config.label}
            </Tag>
            {vehicle.performance && (
              <div className={`${styles.rowPerformance} ${styles[`performance-${tier}`]}`}>
                {vehicle.performance}
              </div>
            )}
          </div>
        </div>
        <div className={styles.rowYourPrice}>
          <div className={styles.rowLabel}>YOUR PRICE:</div>
          <div className={styles.rowValue}>${pricing.yourPrice.toLocaleString()}</div>
        </div>
        <div className={styles.rowMarketAvg}>
          <div className={styles.rowLabel}>TO MARKET:</div>
          <div className={styles.rowValue}>${pricing.marketAvg.toLocaleString()}</div>
          <div className={`${styles.rowDelta} ${pricing.delta >= 0 ? styles.deltaPositive : styles.deltaNegative}`}>
            {pricing.delta >= 0 ? "+" : ""}{pricing.delta.toFixed(1)}%
          </div>
          <div className={styles.rowSold}>Sold: {stats.sold} {stats.soldDelta}</div>
        </div>
        <div className={styles.rowDom}>
          <div className={styles.rowLabel}>DOM:</div>
          <div className={styles.rowValue}>${stats.dom}</div>
        </div>
        <div className={styles.rowRank}>
          <div className={styles.rowRankValue}>RANK: #{stats.rank} {stats.rankDelta}</div>
          <div className={styles.rowRankAlt}>Rank: {stats.rank}-{stats.rank}</div>
        </div>
        <div className={styles.rowDomAlt}>
          <div className={styles.rowLabel}>DOM:</div>
          <div className={styles.rowValue}>${stats.dom}</div>
        </div>
        <div className={styles.rowAdjust}>
          <div className={styles.rowAdjustValue}>ADJUST: ${adjust.amount.toLocaleString()}</div>
          <div className={styles.rowAdjustProfit}>Profit = ${adjust.profit.toLocaleString()}k 1</div>
          <div className={`${styles.rowAdjustFinal} ${adjust.amount >= 0 ? styles.adjustPositive : styles.adjustNegative}`}>
            {adjust.amount >= 0 ? "+" : ""}${adjust.amount.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
};

function getPortfolioHealthFromApi(data) {
  if (!data) return { healthScore: null, eliteCount: 0, riskCount: 0 };
  const ph = data.portfolio_health;
  const score =
    typeof ph === "number"
      ? ph
      : ph && typeof ph === "object" && typeof ph.score === "number"
        ? ph.score
        : data.portfolio_health_score ?? data.score ?? null;
  const buckets = data.bucket_counts;
  const elite =
    buckets?.ELITE?.count ??
    (ph && typeof ph === "object" ? ph.elite : null) ??
    data.elite_count ??
    0;
  const risk =
    buckets?.RISK?.count ??
    (ph && typeof ph === "object" ? ph.risk : null) ??
    data.risk_count ??
    0;
  const numScore = score != null ? Math.max(0, Math.min(100, Number(score))) : null;
  return {
    healthScore: numScore,
    eliteCount: Math.max(0, parseInt(elite, 10) || 0),
    riskCount: Math.max(0, parseInt(risk, 10) || 0),
  };
}

export default function InventoryCommandCenterPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("dom");
  const [portfolioHealthData, setPortfolioHealthData] = useState({
    healthScore: null,
    eliteCount: 0,
    riskCount: 0,
  });

  useEffect(() => {
    fetchPortfolioHealth()
      .then((data) => setPortfolioHealthData(getPortfolioHealthFromApi(data)))
      .catch(() => {});
  }, []);

  const healthScore = portfolioHealthData.healthScore ?? 0;
  const eliteCount = portfolioHealthData.eliteCount;
  const riskCount = portfolioHealthData.riskCount;

  const performanceCards = [
    {
      variant: "elite",
      title: "Elite",
      units: "2 Units",
      capital: "$41,000",
      subtitle: "Turning +20% faster than expected",
      badgeCount: "2",
      hint: "Turning: 28% faster than expected",
      icon: CheckCircleOutlined, // Not used for elite, but required prop
    },
    {
      variant: "strong",
      title: "Strong",
      units: "8 Units",
      capital: "$151,200",
      subtitle: "On pace",
      badgeCount: "$51 / 200",
      hint: "Risk factors",
      icon: CheckCircleOutlined,
    },
    {
      variant: "watch",
      title: "Watch",
      units: "6 Units",
      capital: "$79,800",
      subtitle: "Turning slower than expected 21%",
      badgeCount: "<41 / 221",
      hint: "Adjust pricing",
      icon: WarningOutlined,
    },
    {
      variant: "risk",
      title: "Risk",
      units: "4 Units",
      capital: "$24,700",
      subtitle: "Turning -37% behind pace",
      badgeCount: "<$7 / 238",
      hint: "Recommend to action",
      icon: CloseCircleOutlined,
    },
  ];

  const inventoryRows = [
    {
      tier: "elite",
      vehicle: {
        title: "2020 Honda CR-V",
        miles: "134,600",
        vin: "88484820044162-654196881",
        performance: "Turning 23% faster than expected",
      },
      pricing: {
        yourPrice: 9000,
        marketAvg: 10400,
        delta: 13.5,
      },
      stats: {
        sold: 14,
        soldDelta: "[-1]",
        rank: 1,
        rankDelta: "[-1]",
        dom: 7,
      },
      adjust: {
        amount: 400,
        profit: 1.122,
      },
    },
    {
      tier: "strong",
      vehicle: {
        title: "2014 BMW 5 Series 535i",
        miles: "80,025",
        vin: "WBA5B3C55ED536061",
        performance: "Turning on pace",
      },
      pricing: {
        yourPrice: 12500,
        marketAvg: 13998,
        delta: 10.6,
      },
      stats: {
        sold: 14,
        soldDelta: "[-1]",
        rank: 1,
        rankDelta: "[-1]",
        dom: 1,
      },
      adjust: {
        amount: -1488,
        profit: 12.0,
      },
    },
    {
      tier: "risk",
      vehicle: {
        title: "2016 Ford F150 XLT",
        miles: "69,000",
        vin: "5FNYF6H32GB102253",
        performance: "Turning -37% behind pace",
      },
      pricing: {
        yourPrice: 25800,
        marketAvg: 26000,
        delta: -0.8,
      },
      stats: {
        sold: 43,
        soldDelta: "[-1]",
        rank: 3,
        rankDelta: "[-6]",
        dom: 3,
      },
      adjust: {
        amount: 500,
        profit: 0.5,
      },
    },
  ];

  return (
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Title level={1} className={styles.pageTitle}>
          Inventory Command Center
        </Title>
        <div className={styles.searchContainer}>
          <Input
            placeholder="Search by appearance..."
            prefix={<SearchOutlined className={styles.searchIcon} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
        </div>
      </div>

      {/* Portfolio Health Score */}
      <div className={styles.healthScoreCard}>
        <div className={styles.healthScoreLeft}>
          <div className={styles.healthScoreIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="6" width="12" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.8"/>
              <rect x="6" y="4" width="12" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
              <line x1="8" y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
              <line x1="8" y1="14" x2="16" y2="14" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
            </svg>
          </div>
          <div className={styles.healthScoreContent}>
            <div className={styles.healthScoreTitleRow}>
              <span className={styles.healthScoreTitle}>Portfolio Health Score</span>
              <span className={styles.healthScoreValue}>{healthScore} / 100</span>
            </div>
            <div className={styles.healthScoreBreakdown}>
              {eliteCount} Elite • {riskCount} Risk
            </div>
            <div className={styles.healthScoreGradientBar}></div>
          </div>
        </div>
        <div className={styles.healthScoreRight}>
          <div className={styles.progressContainer}>
            <Progress
              percent={healthScore}
              showInfo={false}
              strokeColor="#38DA9C"
              trailColor="rgba(255, 255, 255, 0.08)"
              className={styles.progressBar}
            />
          </div>
          <div className={styles.progressValue}>{healthScore} / 100</div>
        </div>
      </div>

      {/* Inventory Performance Overview */}
      <div className={styles.performanceSection}>
        <div className={styles.performanceHeader}>
          <Title level={3} className={styles.performanceTitle}>
            Inventory Performance Overview
          </Title>
          <Select
            value={sortBy}
            onChange={setSortBy}
            className={styles.sortSelect}
            size="small"
          >
            <Option value="dom">Sort: DOM</Option>
            <Option value="price">Sort: Price</Option>
            <Option value="rank">Sort: Rank</Option>
          </Select>
        </div>
        <div className={styles.performanceGrid}>
          {performanceCards.map((card) => (
            <MetricCard key={card.variant} {...card} />
          ))}
        </div>
      </div>

      {/* Your Inventory */}
      <div className={styles.inventorySection}>
        <div className={styles.inventoryHeader}>
          <Title level={3} className={styles.inventoryTitle}>
            Your Inventory
          </Title>
          <div className={styles.inventorySubtitle}>
            <ThunderboltOutlined className={styles.subtitleIcon} />
            Elite (20uninst)
          </div>
        </div>
        <div className={styles.inventoryTable}>
          <div className={styles.tableHeader}>
            <div className={styles.headerCell}>VEHICLE</div>
            <div className={styles.headerCell}>YOUR PRICE</div>
            <div className={styles.headerCell}>TO MARKET</div>
            <div className={styles.headerCell}>DOM</div>
            <div className={styles.headerCell}>RANK</div>
            <div className={styles.headerCell}>DOM</div>
            <div className={styles.headerCell}>ADJUST</div>
          </div>
          <div className={styles.tableBody}>
            {inventoryRows.map((row, index) => (
              <InventoryRow key={index} {...row} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
